import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format, differenceInDays } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Fuel, Settings2, Calendar, MapPin, Check, 
  ChevronRight, ChevronLeft, Shield, CreditCard, Info
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [vehicle, setVehicle] = useState(null);
  const [locations, setLocations] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Booking state
  const [pickupLocation, setPickupLocation] = useState(searchParams.get('pickupLocation') || '');
  const [returnLocation, setReturnLocation] = useState(searchParams.get('returnLocation') || '');
  const [dateRange, setDateRange] = useState({
    from: searchParams.get('pickupDate') ? new Date(searchParams.get('pickupDate')) : null,
    to: searchParams.get('returnDate') ? new Date(searchParams.get('returnDate')) : null
  });
  const [pickupTime, setPickupTime] = useState('10:00');
  const [returnTime, setReturnTime] = useState('10:00');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [priceDetails, setPriceDetails] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vehicleRes, locRes] = await Promise.all([
          axios.get(`${API_URL}/api/vehicles/${id}`),
          axios.get(`${API_URL}/api/locations`)
        ]);
        setVehicle(vehicleRes.data);
        setLocations(locRes.data);
        
        // Fetch vehicle-specific add-ons
        const addOnsRes = await axios.get(`${API_URL}/api/vehicles/${id}/addons`);
        setAddOns(addOnsRes.data.addons || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load vehicle details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const calculatePrice = async () => {
      if (!vehicle || !dateRange.from || !dateRange.to || !pickupLocation || !returnLocation) {
        setPriceDetails(null);
        return;
      }

      setCalculating(true);
      try {
        const response = await axios.post(`${API_URL}/api/calculate-price`, null, {
          params: {
            vehicle_id: vehicle.id,
            pickup_date: format(dateRange.from, 'yyyy-MM-dd'),
            return_date: format(dateRange.to, 'yyyy-MM-dd'),
            pickup_location_id: pickupLocation,
            return_location_id: returnLocation,
            add_on_ids: selectedAddOns
          }
        });
        setPriceDetails(response.data);
      } catch (error) {
        console.error('Error calculating price:', error);
      } finally {
        setCalculating(false);
      }
    };
    calculatePrice();
  }, [vehicle, dateRange, pickupLocation, returnLocation, selectedAddOns]);

  const handleDateSelect = (range) => {
    setDateRange(range || { from: null, to: null });
    if (range?.from && range?.to) {
      setTimeout(() => setCalendarOpen(false), 300);
    }
  };

  const rentalDays = dateRange.from && dateRange.to 
    ? differenceInDays(dateRange.to, dateRange.from) 
    : 0;

  const toggleAddOn = (addOnId) => {
    setSelectedAddOns(prev => 
      prev.includes(addOnId) 
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const handleContinue = () => {
    if (!dateRange.from || !dateRange.to || !pickupLocation || !returnLocation) {
      toast.error(i18n.language === 'fr' 
        ? 'Veuillez remplir tous les détails de réservation' 
        : 'Please fill in all booking details');
      return;
    }

    const params = new URLSearchParams();
    params.set('vehicleId', vehicle.id);
    params.set('pickupLocation', pickupLocation);
    params.set('returnLocation', returnLocation);
    params.set('pickupDate', format(dateRange.from, 'yyyy-MM-dd'));
    params.set('returnDate', format(dateRange.to, 'yyyy-MM-dd'));
    params.set('pickupTime', pickupTime);
    params.set('returnTime', returnTime);
    if (selectedAddOns.length > 0) {
      params.set('addOns', selectedAddOns.join(','));
    }
    
    navigate(`/booking/checkout/${vehicle.id}?${params.toString()}`);
  };

  const getLocalizedName = (item) => {
    return i18n.language === 'fr' ? item.name_fr : item.name_en;
  };

  const getLocalizedFeatures = () => {
    return i18n.language === 'fr' ? vehicle?.features_fr : vehicle?.features_en;
  };

  const getLocalizedDescription = () => {
    return i18n.language === 'fr' ? vehicle?.description_fr : vehicle?.description_en;
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-container py-20 text-center">
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  if (!vehicle) {
    return (
      <Layout>
        <div className="section-container py-20 text-center">
          <p className="text-gray-500">
            {i18n.language === 'fr' ? 'Véhicule non trouvé' : 'Vehicle not found'}
          </p>
          <Button onClick={() => navigate('/vehicles')} className="mt-4 btn-outline">
            {i18n.language === 'fr' ? 'Retour à la flotte' : 'Back to Fleet'}
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="border-b border-gray-200">
        <div className="section-container py-4">
          <div className="flex items-center gap-2 text-sm">
            <button 
              onClick={() => navigate('/vehicles')}
              className="text-gray-400 hover:text-[#181B1F] transition-colors"
            >
              {t('nav.fleet')}
            </button>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <span className="text-[#181B1F]">{vehicle.name}</span>
          </div>
        </div>
      </div>

      <div className="section-container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Vehicle Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div
              className="aspect-[16/9] overflow-hidden rounded-sm"
              data-testid="vehicle-image"
            >
              <img
                src={vehicle.images[0] || 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=1200'}
                alt={vehicle.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Vehicle Info */}
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className="bg-primary text-[#181B1F] mb-3">
                    {i18n.language === 'fr' ? vehicle.category_name_fr : vehicle.category_name_en}
                  </Badge>
                  <h1 className="font-serif text-4xl md:text-5xl text-[#181B1F] font-light">
                    {vehicle.name}
                  </h1>
                  <p className="text-gray-400 mt-2">{vehicle.brand} • {vehicle.model} • {vehicle.year}</p>
                </div>
                <div className="text-right">
                  <span className="text-gray-400 text-sm">{t('vehicle.from')}</span>
                  <p className="text-3xl text-[#181B1F] font-light">
                    CHF {vehicle.base_daily_rate}
                    <span className="text-gray-400 text-sm">{t('vehicle.perDay')}</span>
                  </p>
                </div>
              </div>

              <p className="text-gray-500 text-lg leading-relaxed">
                {getLocalizedDescription()}
              </p>

              {/* Specifications */}
              <div className="card-feature" data-testid="vehicle-specs">
                <h3 className="font-serif text-xl text-[#181B1F] mb-4">{t('vehicle.specifications')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <Users className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-xs uppercase tracking-wider">Seats</span>
                    </div>
                    <p className="text-[#181B1F] text-lg">{vehicle.seats}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <Settings2 className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-xs uppercase tracking-wider">{t('vehicle.transmission')}</span>
                    </div>
                    <p className="text-[#181B1F] text-lg">
                      {vehicle.transmission === 'automatic' ? t('vehicle.automatic') : t('vehicle.manual')}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <Fuel className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-xs uppercase tracking-wider">{t('vehicle.fuelType')}</span>
                    </div>
                    <p className="text-[#181B1F] text-lg capitalize">{vehicle.fuel_type}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <span className="text-xs uppercase tracking-wider">{t('vehicle.doors')}</span>
                    </div>
                    <p className="text-[#181B1F] text-lg">{vehicle.doors}</p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="card-feature" data-testid="vehicle-features">
                <h3 className="font-serif text-xl text-[#181B1F] mb-4">{t('vehicle.features')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {getLocalizedFeatures()?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary" strokeWidth={2} />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Deposit Info */}
              <div className="card-feature bg-primary/5 border-primary/20" data-testid="deposit-info">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#181B1F] mb-1">{t('vehicle.deposit')}</h3>
                    <p className="text-2xl text-primary font-light mb-2">
                      CHF {vehicle.deposit_amount?.toLocaleString() || '1,500'}
                    </p>
                    <p className="text-gray-500 text-sm flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      {t('vehicle.depositNote')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Tiers */}
              {vehicle.pricing_tiers?.length > 0 && (
                <div className="card-feature" data-testid="pricing-tiers">
                  <h3 className="font-serif text-xl text-[#181B1F] mb-4">
                    {i18n.language === 'fr' ? 'Tarifs Dégressifs' : 'Tiered Pricing'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {vehicle.pricing_tiers.map((tier, index) => (
                      <div 
                        key={index} 
                        className="bg-white/5 p-4 text-center border border-gray-200 rounded-sm"
                      >
                        <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                          {tier.min_days === tier.max_days 
                            ? `${tier.min_days} ${i18n.language === 'fr' ? 'jour' : 'day'}` 
                            : tier.max_days === 999 
                              ? `${tier.min_days}+ ${i18n.language === 'fr' ? 'jours' : 'days'}`
                              : `${tier.min_days}-${tier.max_days} ${i18n.language === 'fr' ? 'jours' : 'days'}`
                          }
                        </p>
                        <p className="text-2xl text-[#181B1F] font-light">
                          CHF {Number(tier.daily_rate).toFixed(2).replace(/\.?0+$/, '')}
                          <span className="text-gray-400 text-sm">
                            /{i18n.language === 'fr' ? 'jour' : 'day'}
                          </span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="glass-surface p-6 space-y-6 sticky top-28" data-testid="booking-card">
              <h3 className="font-serif text-2xl text-[#181B1F]">
                {i18n.language === 'fr' ? 'Réserver ce Véhicule' : 'Book This Vehicle'}
              </h3>

              {/* Location Selects */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    {t('search.pickupLocation')}
                  </label>
                  <Select value={pickupLocation} onValueChange={setPickupLocation}>
                    <SelectTrigger className="input-default" data-testid="detail-pickup-location">
                      <SelectValue placeholder={t('search.pickupLocation')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200">
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-[#181B1F]">
                          {getLocalizedName(loc)}
                          {loc.delivery_fee > 0 && (
                            <span className="text-gray-400 ml-2">(+CHF {loc.delivery_fee})</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    {t('search.returnLocation')}
                  </label>
                  <Select value={returnLocation} onValueChange={setReturnLocation}>
                    <SelectTrigger className="input-default" data-testid="detail-return-location">
                      <SelectValue placeholder={t('search.returnLocation')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200">
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-[#181B1F]">
                          {getLocalizedName(loc)}
                          {loc.delivery_fee > 0 && (
                            <span className="text-gray-400 ml-2">(+CHF {loc.delivery_fee})</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date Range Picker */}
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-gray-500">
                  {t('search.selectDateRange')}
                </label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start input-default border text-left h-14",
                        !dateRange.from && "text-gray-400"
                      )}
                      data-testid="detail-date-range"
                    >
                      <Calendar className="mr-3 h-5 w-5 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        {dateRange.from ? (
                          dateRange.to ? (
                            <div className="flex items-center justify-between">
                              <span className="text-[#181B1F] truncate">
                                {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}
                              </span>
                              {rentalDays > 0 && (
                                <span className="text-primary font-medium ml-2">
                                  {rentalDays} {t('booking.days')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[#181B1F]">{format(dateRange.from, "PP")}</span>
                          )
                        ) : (
                          t('search.selectDates')
                        )}
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white shadow-lg border-gray-200" align="center">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={handleDateSelect}
                      numberOfMonths={2}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Pickup & Return Time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    {t('booking.pickupTime', 'Pickup Time')}
                  </label>
                  <Select value={pickupTime} onValueChange={setPickupTime}>
                    <SelectTrigger className="input-default" data-testid="detail-pickup-time">
                      <SelectValue placeholder="10:00" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200 max-h-60">
                      {Array.from({ length: 37 }, (_, i) => {
                        const totalMinutes = 6 * 60 + i * 30;
                        const hours = Math.floor(totalMinutes / 60) % 24;
                        const minutes = totalMinutes % 60;
                        const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                        const isLate = hours >= 20 || hours < 6;
                        return (
                          <SelectItem key={timeStr} value={timeStr} className="text-[#181B1F]">
                            {timeStr}{isLate ? ' (+CHF 15)' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {pickupTime && parseInt(pickupTime.split(':')[0]) >= 20 && (
                    <p className="text-yellow-400 text-xs">+CHF 15 late pickup fee</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    {t('booking.returnTime', 'Return Time')}
                  </label>
                  <Select value={returnTime} onValueChange={setReturnTime}>
                    <SelectTrigger className="input-default" data-testid="detail-return-time">
                      <SelectValue placeholder="10:00" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-lg border-gray-200 max-h-60">
                      {Array.from({ length: 37 }, (_, i) => {
                        const totalMinutes = 6 * 60 + i * 30;
                        const hours = Math.floor(totalMinutes / 60) % 24;
                        const minutes = totalMinutes % 60;
                        const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                        const isAfterHours = hours >= 20 || hours < 6;
                        return (
                          <SelectItem key={timeStr} value={timeStr} className="text-[#181B1F]">
                            {timeStr}{isAfterHours ? ' (Agency dropbox)' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {returnTime && parseInt(returnTime.split(':')[0]) >= 20 && (
                    <p className="text-gray-500 text-xs">After-hours: Key dropbox at agency</p>
                  )}
                </div>
              </div>

              {/* Add-ons */}
              <div className="space-y-3">
                <label className="text-xs uppercase tracking-wider text-gray-500">
                  {t('booking.addOns')}
                </label>
                <div className="space-y-2">
                  {addOns.map((addOn) => (
                    <div 
                      key={addOn.id}
                      className={cn(
                        "flex items-center justify-between p-3 border rounded-sm cursor-pointer transition-all",
                        selectedAddOns.includes(addOn.id)
                          ? "border-primary/50 bg-primary/10"
                          : "border-gray-200 hover:border-white/20"
                      )}
                      onClick={() => toggleAddOn(addOn.id)}
                      data-testid={`addon-${addOn.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={selectedAddOns.includes(addOn.id)}
                          className="border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <span className="text-[#181B1F] text-sm">{getLocalizedName(addOn)}</span>
                      </div>
                      <span className="text-gray-500 text-sm">
                        +CHF {addOn.price || addOn.price_per_day || 0}
                        {(addOn.is_daily !== false && !addOn.one_time_fee) && `/${i18n.language === 'fr' ? 'jour' : 'day'}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              {priceDetails && (
                <div className="border-t border-gray-200 pt-4 space-y-2" data-testid="price-summary">
                  <div className="flex justify-between text-gray-500 text-sm">
                    <span>{priceDetails.rental_days} {t('booking.days')} × CHF {priceDetails.daily_rate}</span>
                    <span>CHF {(priceDetails.daily_rate * priceDetails.rental_days).toFixed(2)}</span>
                  </div>
                  {priceDetails.add_ons_total > 0 && (
                    <div className="flex justify-between text-gray-500 text-sm">
                      <span>{t('booking.addOns')}</span>
                      <span>CHF {priceDetails.add_ons_total.toFixed(2)}</span>
                    </div>
                  )}
                  {(priceDetails.pickup_fee > 0 || priceDetails.return_fee > 0) && (
                    <div className="flex justify-between text-gray-500 text-sm">
                      <span>{i18n.language === 'fr' ? 'Frais de livraison' : 'Delivery fees'}</span>
                      <span>CHF {(priceDetails.pickup_fee + priceDetails.return_fee).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[#181B1F] text-lg pt-2 border-t border-gray-200">
                    <span>{t('booking.total')}</span>
                    <span className="font-medium">CHF {priceDetails.total_price.toFixed(2)}</span>
                  </div>
                  
                  {/* Deposit Info */}
                  <div className="flex justify-between text-primary/80 text-sm pt-2">
                    <span className="flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      {t('booking.deposit')}
                    </span>
                    <span>CHF {vehicle.deposit_amount?.toLocaleString() || '1,500'}</span>
                  </div>
                  <p className="text-gray-400 text-xs">{t('booking.depositInfo')}</p>
                </div>
              )}

              {/* Continue Button */}
              <Button 
                onClick={handleContinue}
                disabled={!dateRange.from || !dateRange.to || !pickupLocation || !returnLocation}
                className="w-full btn-primary h-14 text-base"
                data-testid="continue-btn"
              >
                {t('booking.continue')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VehicleDetailPage;
